/**
 * Theme Management and Configuration
 *
 * Handles theme mode selection (light/dark), theme persistence,
 * accent color application, and dynamic color updates.
 */

import {
    parseColor,
    rgbToString,
    getReadableAccentColor,
    getContrastColor,
} from './colors';

/** Theme mode type */
export type ThemeMode = 'light' | 'dark' | 'liquid-glass' | 'dark-liquid-glass' | 'dark-purple-liquid-glass' | 'dark-cyan-neon' | 'dark-green-neon' | 'dark-synthwave' | 'soccer-green' | 'soccer-green-liquid-glass' | 'soccer-cyber';

/** Team colors interface */
export interface TeamColors {
    primary: string;
    secondary: string;
}

/* ========== THEME MODE MANAGEMENT ========== */

/**
 * Get current theme mode from localStorage or system preference
 * @returns Current theme mode ('light', 'dark', 'liquid-glass', 'dark-liquid-glass', 'dark-purple-liquid-glass', 'dark-cyan-neon', 'dark-green-neon', 'dark-synthwave', 'soccer-green', 'soccer-green-liquid-glass')
 */
export const getCurrentTheme = (): ThemeMode => {
    const savedTheme = localStorage.getItem('psm_theme') as ThemeMode | null;
    if (savedTheme) {
        return savedTheme;
    }

    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark-liquid-glass';
    }

    return 'dark-liquid-glass'; // Default to dark-liquid-glass theme
};

/**
 * Set theme mode and apply it to the document
 * @param mode - Theme mode to apply
 */
export const setThemeMode = (mode: ThemeMode): void => {
    localStorage.setItem('psm_theme', mode);
    document.documentElement.classList.remove('theme-light', 'theme-dark', 'theme-liquid-glass', 'theme-dark-liquid-glass', 'theme-dark-purple-liquid-glass', 'theme-dark-cyan-neon', 'theme-dark-green-neon', 'theme-dark-synthwave', 'theme-soccer-green', 'theme-soccer-green-liquid-glass', 'theme-soccer-cyber');
    document.documentElement.classList.add(`theme-${mode}`);
};

/**
 * Toggle between themes: dark -> light -> liquid-glass -> dark-liquid-glass -> dark-purple-liquid-glass -> dark-cyan-neon -> dark-green-neon -> dark-synthwave -> soccer-green -> soccer-green-liquid-glass -> soccer-cyber -> dark (cycle)
 */
export const toggleTheme = (): void => {
    const current = getCurrentTheme();
    let next: ThemeMode;

    switch (current) {
        case 'light':
            next = 'liquid-glass';
            break;
        case 'liquid-glass':
            next = 'dark-liquid-glass';
            break;
        case 'dark-liquid-glass':
            next = 'dark-purple-liquid-glass';
            break;
        case 'dark-purple-liquid-glass':
            next = 'dark-cyan-neon';
            break;
        case 'dark-cyan-neon':
            next = 'dark-green-neon';
            break;
        case 'dark-green-neon':
            next = 'dark-synthwave';
            break;
        case 'dark-synthwave':
            next = 'soccer-green';
            break;
        case 'soccer-green':
            next = 'soccer-green-liquid-glass';
            break;
        case 'soccer-green-liquid-glass':
            next = 'soccer-cyber';
            break;
        case 'soccer-cyber':
            next = 'dark';
            break;
        default:
            next = 'light';
    }

    setThemeMode(next);
};

/* ========== THEME COLOR MANAGEMENT ========== */

/**
 * Get theme colors (background, text, etc.) for current theme
 * @returns Object with theme colors for current mode
 */
export const getThemeColors = () => {
    const root = document.documentElement;
    const style = getComputedStyle(root);

    return {
        primaryColor: style.getPropertyValue('--primary-color').trim(),
        secondaryColor: style.getPropertyValue('--secondary-color').trim(),
        backgroundColor: style.getPropertyValue('--background-color').trim(),
        textColor: style.getPropertyValue('--text-color').trim(),
        highlightColor: style.getPropertyValue('--highlight-color').trim(),
    };
};

/* ========== ACCENT COLOR APPLICATION ========== */

/**
 * Apply accent colors (team colors) to the root CSS variables
 * This updates --accent-color, --accent-color-rgb, and related variables
 * @param teamColors - Team colors to apply, or undefined to reset to defaults
 */
export const setAccentColors = (teamColors: TeamColors | undefined): void => {
    const rootStyle = document.documentElement.style;
    const currentTheme = getCurrentTheme();

    if (!teamColors) {
        // Reset to default accent color
        rootStyle.setProperty('--accent-color', '#00ff9b');
        rootStyle.setProperty('--accent-color-rgb', '0, 255, 155');
        rootStyle.setProperty('--secondary-accent-color', currentTheme === 'light' ? '#ffffff' : '#1e1e1e');
        rootStyle.setProperty('--accent-text-color', '#00ff9b');
        return;
    }

    // Get primary color and calculate variants
    const primaryColorHex = teamColors.primary || '#00ff9b';
    const primaryRgb = parseColor(primaryColorHex);

    // Get current background color for contrast calculations
    const themeColors = getThemeColors();
    const bgColorString = themeColors.backgroundColor;

    // Calculate readable accent color for text on backgrounds
    const accentTextColor = getReadableAccentColor(primaryColorHex, bgColorString, 4.5);

    // Calculate text color for accent backgrounds (buttons, etc)
    const secondaryColor = getContrastColor(primaryColorHex);

    // Apply all accent color variants
    rootStyle.setProperty('--accent-color', primaryColorHex);
    rootStyle.setProperty('--accent-color-rgb', rgbToString(primaryRgb));
    rootStyle.setProperty('--secondary-accent-color', secondaryColor);
    rootStyle.setProperty('--accent-text-color', accentTextColor);
};

/**
 * Apply player header color (always the player's team color)
 * This is separate from accent colors so header/sidebar don't change when viewing other teams
 * @param teamColors - Player's team colors
 */
export const setPlayerHeaderColor = (teamColors: TeamColors | undefined): void => {
    const rootStyle = document.documentElement.style;
    const themeColors = getThemeColors();

    if (!teamColors) {
        rootStyle.setProperty('--player-header-color', 'var(--accent-text-color)');
        return;
    }

    const playerTeamPrimary = teamColors.primary || '#00ff9b';
    const playerHeaderColor = getReadableAccentColor(playerTeamPrimary, themeColors.backgroundColor);
    rootStyle.setProperty('--player-header-color', playerHeaderColor);
};

/**
 * Apply content area accent colors
 * These change when viewing different teams but don't affect header/sidebar
 * @param teamColors - Team colors for content area
 */
export const setContentAccentColors = (teamColors: TeamColors | undefined): void => {
    const rootStyle = document.documentElement.style;

    if (!teamColors) {
        const primaryHex = '#00ff9b';
        const primaryRgb = parseColor(primaryHex);
        rootStyle.setProperty('--content-accent-color', primaryHex);
        rootStyle.setProperty('--content-accent-color-rgb', rgbToString(primaryRgb));
        return;
    }

    const primaryColorHex = teamColors.primary || '#00ff9b';
    const primaryRgb = parseColor(primaryColorHex);

    rootStyle.setProperty('--content-accent-color', primaryColorHex);
    rootStyle.setProperty('--content-accent-color-rgb', rgbToString(primaryRgb));
};

/* ========== THEME PERSISTENCE ========== */

/**
 * Get user's saved theme preferences
 * @returns Object with theme preferences
 */
export const getSavedThemePreferences = () => {
    return {
        theme: getCurrentTheme(),
        font: localStorage.getItem('psm_font') || "'Silkscreen', sans-serif",
        fontSize: localStorage.getItem('psm_fontSize') || '12px',
    };
};

/**
 * Save theme preferences
 * @param preferences - Theme preferences to save
 */
export const saveThemePreferences = (preferences: {
    theme?: ThemeMode;
    font?: string;
    fontSize?: string;
}): void => {
    if (preferences.theme) {
        setThemeMode(preferences.theme);
    }
    if (preferences.font) {
        localStorage.setItem('psm_font', preferences.font);
        document.documentElement.style.setProperty('--font-family', preferences.font);
    }
    if (preferences.fontSize) {
        localStorage.setItem('psm_fontSize', preferences.fontSize);
        document.documentElement.style.setProperty('--font-size', preferences.fontSize);
    }
};

/* ========== THEME INITIALIZATION ========== */

/**
 * Initialize theme on application load
 * Sets up theme mode, applies saved preferences, and listens for system theme changes
 */
export const initializeTheme = (): void => {
    const currentTheme = getCurrentTheme();
    setThemeMode(currentTheme);

    // Apply saved font settings
    const savedFont = localStorage.getItem('psm_font') || "'Silkscreen', sans-serif";
    const savedFontSize = localStorage.getItem('psm_fontSize') || '12px';

    document.documentElement.style.setProperty('--font-family', savedFont);
    document.documentElement.style.setProperty('--font-size', savedFontSize);

    // Listen for system theme changes
    if (window.matchMedia) {
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
            // Only apply if user hasn't manually set a theme
            if (!localStorage.getItem('psm_theme')) {
                setThemeMode(e.matches ? 'dark' : 'light');
            }
        });
    }
};
